/* assets/custom-variant-ui.js */
(() => {
  if (window.__customVariantUIInitialized) return;
  window.__customVariantUIInitialized = true;

  // The frame colour lives in the Shopify option "Style". Every value is a frame
  // colour such as "Black frame", except the one meaning "sold unframed". The
  // custom UI carries these RAW Shopify values, so none is ever rebuilt here.
  //
  // That "no frame" value is a bare dash today, but it may be renamed in Shopify
  // to something that reads properly at checkout — a page the theme cannot
  // reach. Both spellings are accepted: were only the dash recognised, renaming
  // it would make an unframed choice resolve a FRAMED variant, at the framed
  // price. Keep in step with snippets/frame-option-label.liquid.
  const NO_FRAME_WORDS = new Set([
    "none",
    "no",
    "no frame",
    "noframe",
    "unframed",
    "sans cadre",
  ]);
  const isNoFrameValue = (v) => {
    const t = String(v == null ? "" : v).trim();
    // Empty, or nothing but dashes: hyphen, figure/en/em dash, bar, minus.
    if (/^[-\u2012\u2013\u2014\u2015\u2212]*$/.test(t)) return true;
    return NO_FRAME_WORDS.has(t.toLowerCase().replace(/\s+/g, " "));
  };

  const variantsCache = new WeakMap();
  const syncing = new WeakSet();

  const normalize = (s) =>
    String(s || "")
      .replace(/[\u00A0\u202F]/g, " ")
      .replace(/\s+/g, " ")
      .replace(/[""″]/g, '"')
      .replace(/['']/g, "'")
      .replace(/×/g, "x")
      .replace(/,/g, ".")
      .trim()
      .toLowerCase();

  const dispatchNativeChange = (el) => {
    if (!el) return;
    
    el.dispatchEvent(new Event("input", { bubbles: true, cancelable: true }));
    el.dispatchEvent(new Event("change", { bubbles: true, cancelable: true }));
    
    if (document.activeElement !== el) {
      el.focus();
      el.blur();
    }
  };

  // Réparer le reset de sélection provoqué par Dawn sous WebKit.
  //
  // `VariantSelects.updateSelectionMetadata` retire l'attribut `selected` de
  // l'option qui le porte avant de le reposer. Quand cette option est celle qui
  // est SÉLECTIONNÉE, le retrait remet sa `selectedness` à false et le <select>
  // « ask for a reset » : il retombe sur sa première option (HTML Standard,
  // étapes de retrait d'attribut de <option>). Blink n'applique pas cette
  // étape, WebKit si — mesuré sur Safari, l'axe Style repartait sur « No frame »
  // alors que l'axe Product disait « Framed », d'où une variante inexistante et
  // un bouton « unavailable ».
  //
  // On ne peut pas s'en prémunir en posant le drapeau « dirtiness » via
  // `option.selected = true` : WebKit ne l'honore pas quand l'option est déjà
  // sélectionnée. On répare donc après coup. `selectedOptionValues` est lu
  // APRÈS updateSelectionMetadata, donc la réparation arrive à temps pour
  // l'URL du fetch.
  //
  // Sans effet là où le reset ne se produit pas : on ne touche à rien si
  // l'index n'a pas bougé.
  function patchVariantSelectsReset() {
    const VS = window.customElements?.get("variant-selects");
    if (!VS || VS.prototype.__mobeSelectedResetGuard) return;

    const orig = VS.prototype.updateSelectionMetadata;
    if (typeof orig !== "function") return;

    VS.prototype.__mobeSelectedResetGuard = true;
    VS.prototype.updateSelectionMetadata = function (event) {
      const target = event?.target;
      const isSelect = target?.tagName === "SELECT";
      const before = isSelect ? target.selectedIndex : null;

      const out = orig.call(this, event);

      if (isSelect && target.selectedIndex !== before && before != null && before >= 0) {
        target.selectedIndex = before;
        [...target.options].forEach((o, i) => {
          if (i === before) o.setAttribute("selected", "selected");
          else o.removeAttribute("selected");
        });
      }

      return out;
    };
  }

  // Neutraliser le double abonnement de <variant-selects>.
  //
  // Mesuré en console : un seul <variant-selects> sur la page, mais
  // `updateSelectionMetadata` y passe DEUX fois pour un seul `change`. La
  // classe ajoute son écouteur dans `connectedCallback` et n'a ni
  // `disconnectedCallback` ni `removeEventListener` : chaque reconnexion
  // empile donc un écouteur de plus, définitivement. Résultat, deux requêtes
  // de section identiques par changement de variante, et sous WebKit la
  // seconde déclenchait le reset de sélection réparé plus haut.
  //
  // Deux verrous, parce qu'ils ne couvrent pas la même fenêtre :
  //
  // 1. `renderProductInfo` ignore une seconde demande identique émise dans le
  //    même tick. C'est ce qui corrige l'élément DÉJÀ doublé au moment où ce
  //    script s'exécute — on ne peut pas retirer un écouteur anonyme ajouté
  //    avant nous.
  //
  //    Pourquoi ici et pas sur `handleOptionValueChange`, qui serait le point
  //    naturel : `product-info` s'abonne avec
  //    `subscribe(..., this.handleOptionValueChange.bind(this))` dans son
  //    `connectedCallback`. `bind` fige la fonction au moment de l'abonnement,
  //    qui a lieu à l'upgrade de l'élément, donc avant nous. L'abonné détient
  //    une copie liée de la méthode d'ORIGINE, qu'un patch de prototype
  //    n'atteint jamais. `renderProductInfo`, lui, est appelé via
  //    `this.renderProductInfo(...)` : la résolution se fait sur le prototype
  //    au moment de l'appel, donc le patch s'applique. Mesuré : la première
  //    version, posée sur handleOptionValueChange, était inerte en production.
  //
  // 2. `connectedCallback` devient idempotent par élément, ce qui empêche la
  //    pile de grossir aux reconnexions suivantes — `viewTransition` remplace
  //    variant-selects à chaque changement de variante. Sans effet de bord :
  //    ce `connectedCallback` ne fait rien d'autre qu'ajouter l'écouteur.
  //
  // La cause du tout premier doublement, au chargement, reste à identifier.
  function patchDoubleSubscription() {
    const PI = window.customElements?.get("product-info");
    if (PI && !PI.prototype.__mobeDedupe) {
      const orig = PI.prototype.renderProductInfo;
      if (typeof orig === "function") {
        PI.prototype.__mobeDedupe = true;
        PI.prototype.renderProductInfo = function (args) {
          // Les deux abonnements publient la même URL dans le même tick. La clé
          // est relâchée en microtâche : un retour ultérieur sur la même
          // variante doit bien redéclencher un rendu.
          if (this.__mobePendingRender === args?.requestUrl) return;
          this.__mobePendingRender = args?.requestUrl;
          queueMicrotask(() => {
            this.__mobePendingRender = null;
          });
          return orig.call(this, args);
        };
      }
    }

    const VS = window.customElements?.get("variant-selects");
    if (VS && !VS.prototype.__mobeConnectGuard) {
      const orig = VS.prototype.connectedCallback;
      if (typeof orig === "function") {
        VS.prototype.__mobeConnectGuard = true;
        VS.prototype.connectedCallback = function () {
          if (this.__mobeVSConnected) return;
          this.__mobeVSConnected = true;
          return orig.call(this);
        };
      }
    }
  }

  function closestProductInfo(el) {
    return el?.closest("product-info") || document.querySelector("product-info");
  }

  function getVariantSelects(productInfo) {
    return productInfo?.querySelector("variant-selects");
  }

  function getVariants(variantSelects) {
    if (!variantSelects) return null;
    if (variantsCache.has(variantSelects)) return variantsCache.get(variantSelects);

    const script = variantSelects.querySelector(
      'script[type="application/json"]:not([data-selected-variant])'
    );
    if (!script) return null;

    const variants = JSON.parse(script.textContent.trim());
    variantsCache.set(variantSelects, variants);
    return variants;
  }

  function getSelectedVariantFromScript(variantSelects) {
    const script = variantSelects?.querySelector('script[data-selected-variant]');
    if (!script) return undefined;
    try {
      return JSON.parse(script.textContent.trim());
    } catch {
      return undefined;
    }
  }

  function getGroupName(container) {
    const legend = container.querySelector("legend");
    const label = container.querySelector("label.form__label");
    const t = (legend || label)?.textContent || "";
    return normalize(t).split(":")[0].trim();
  }

  function getGroupPosition(container) {
    const select = container.querySelector("select");
    if (select?.id) {
      const m = select.id.match(/-(\d+)$/);
      if (m) return parseInt(m[1], 10) + 1;
    }

    const radio = container.querySelector('input[type="radio"]');
    if (radio?.name) {
      const m = radio.name.match(/-(\d+)$/);
      if (m) return parseInt(m[1], 10);
    }

    return null;
  }

  function getOptionGroups(variantSelects) {
    const containers = [...variantSelects.querySelectorAll(".product-form__input")];

    const groups = containers
      .map((c) => {
        const name = getGroupName(c);
        const position = getGroupPosition(c);
        if (!name || !position) return null;

        const select = c.querySelector("select");
        if (select) {
          const values = [...select.options].map((o) => o.value);
          return { name, position, type: "select", container: c, input: select, values };
        }

        const radios = [...c.querySelectorAll('input[type="radio"]')];
        if (radios.length) {
          const values = radios.map((r) => r.value);
          return { name, position, type: "radio", container: c, radios, values };
        }

        return null;
      })
      .filter(Boolean);

    groups.sort((a, b) => a.position - b.position);
    return groups;
  }

  function findGroup(groups, wantedName) {
    const w = normalize(wantedName);
    return groups.find((g) => g.name === w);
  }

  // Finds the option group carrying the framed/unframed distinction: the Shopify
  // option "Product" ("Fine Art Print" / "Framed Fine Art Print"). There is no
  // option named "Frame" — the frame COLOUR is a separate option, "Style".
  function findFrameGroup(groups) {
    const productGroup = findGroup(groups, "Product");
    if (productGroup && productGroup.values.some((v) => normalize(v).includes("framed"))) {
      return productGroup;
    }

    return null;
  }

  // Returns the value representing a framed product from the frame group.
  function getFramedValue(frameGroup) {
    const explicit = pickExisting(frameGroup.values, ["Framed"]);
    if (explicit) return explicit;
    // "Product" style: find the value that contains "framed" but not "unframed"
    return (
      frameGroup.values.find(
        (v) => !normalize(v).includes("unframed") && normalize(v).includes("framed")
      ) || null
    );
  }

  // Returns the value representing an unframed product from the frame group.
  function getUnframedValue(frameGroup) {
    const explicit = pickExisting(frameGroup.values, ["Unframed"]);
    if (explicit) return explicit;
    // "Product" style: find the value that does NOT contain "framed"
    return frameGroup.values.find((v) => !normalize(v).includes("framed")) || null;
  }

  function getSelectedValue(group) {
    if (!group) return null;
    if (group.type === "select") return group.input.value;
    const checked = group.radios.find((r) => r.checked);
    return checked ? checked.value : group.values?.[0] ?? null;
  }

  function setGroupValue(group, value) {
    if (!group || value == null) return null;

    if (group.type === "select") {
      const before = group.input.value;
      group.input.value = value;
      if (group.input.value !== value) {
        const opt = [...group.input.options].find((o) => normalize(o.value) === normalize(value));
        if (opt) group.input.value = opt.value;
      }
      return before !== group.input.value ? group.input : group.input;
    }

    const r = group.radios.find((x) => x.value === value);
    if (r) r.checked = true;
    return r || null;
  }

  function pickExisting(values, candidates) {
    const lower = values.map((v) => normalize(v));
    for (const cand of candidates) {
      const idx = lower.indexOf(normalize(cand));
      if (idx !== -1) return values[idx];
    }
    return null;
  }

  function findVariantByOptions(variants, optionsArr) {
    const desired = (optionsArr || []).map(normalize);
    return (
      variants.find((v) => {
        const opts = (v.options || []).map(normalize);
        if (opts.length !== desired.length) return false;
        for (let i = 0; i < desired.length; i++) {
          if (opts[i] !== desired[i]) return false;
        }
        return true;
      }) || null
    );
  }

  function bestVariantForCurrentSelection(variants, groups) {
    if (!variants?.length) return null;

    const selected = groups.map((g) => getSelectedValue(g));
    let exact = findVariantByOptions(variants, selected);
    if (exact) return exact;

    for (let k = selected.length - 1; k >= 1; k--) {
      const prefix = selected.slice(0, k).map(normalize);
      const pool = variants.filter((v) => {
        const o = (v.options || []).map(normalize);
        for (let i = 0; i < k; i++) {
          if (o[i] !== prefix[i]) return false;
        }
        return true;
      });
      const pick = pool.find((v) => v.available) || pool[0];
      if (pick) return pick;
    }

    return variants.find((v) => v.available) || variants[0] || null;
  }

  // `silent` : pose les valeurs sans émettre de `change`, donc sans déclencher
  // de requête. Sert à restaurer l'état après un échec réseau — dispatcher là
  // relancerait la requête qui vient d'échouer, et bouclerait.
  function setNativeToVariant(variantSelects, groups, targetVariant, silent) {
    if (!variantSelects || !groups?.length || !targetVariant) return;

    const changedElements = [];
    groups.forEach((g) => {
      const desired = (targetVariant.options || [])[g.position - 1];
      if (desired == null) return;

      if (g.type === "select") {
        if (g.input.value !== desired) {
          g.input.value = desired;
          if (g.input.value !== desired) {
            const opt = [...g.input.options].find((o) => normalize(o.value) === normalize(desired));
            if (opt) g.input.value = opt.value;
          }
          changedElements.push(g.input);
        }
      } else {
        const r = g.radios.find((x) => x.value === desired);
        if (r && !r.checked) {
          r.checked = true;
          changedElements.push(r);
        }
      }
    });

    // Déplacer l'attribut `selected` nous-mêmes, sur chaque select modifié.
    //
    // variant-selects construit l'URL du fetch à partir de
    // `select option[selected]` — l'ATTRIBUT, pas la propriété. Assigner .value
    // ne le déplace pas, et Dawn ne le rattrape que pour la cible de
    // l'événement. Sans ce rattrapage, un changement portant sur plusieurs axes
    // partait avec la valeur périmée de l'axe non ciblé.
    //
    // RÈGLE À NE PAS ENFREINDRE : on ne retire jamais l'attribut `selected` de
    // l'option actuellement sélectionnée. Le retirer d'une option dont le
    // drapeau « dirtiness » est à false remet sa `selectedness` à false, puis
    // le <select> « ask for a reset » et se rabat sur sa première option (HTML
    // Standard, étapes de retrait d'attribut de <option>). Blink n'applique pas
    // cette étape, WebKit si.
    //
    // Cela vaut aussi pour Dawn : `updateSelectionMetadata` commence par
    // retirer l'attribut de l'option qui le porte avant de le reposer. Si on le
    // posait nous-mêmes sur l'option choisie de la cible du dispatch, Dawn le
    // retirerait de cette option — et déclencherait le reset. D'où le
    // traitement distinct ci-dessous : sur la cible, on laisse l'attribut
    // absent, le `find()` de Dawn ne trouve rien, son `?.` court-circuite le
    // retrait, et il pose l'attribut au bon endroit lui-même.
    //
    // Les radios n'ont pas ce problème : selectedOptionValues les lit via
    // :checked, qui reflète bien la propriété.
    const lastChanged = changedElements[changedElements.length - 1];

    changedElements.forEach((el) => {
      if (el.tagName !== "SELECT") return;

      const chosen = el.selectedOptions[0];
      if (!chosen) return;

      [...el.options].forEach((o) => {
        if (o !== chosen) o.removeAttribute("selected");
      });

      // Hors dispatch, Dawn ne posera pas l'attribut sur la cible : on le fait
      // nous-mêmes, sinon cet axe partirait sans valeur à la requête suivante.
      if (silent || el !== lastChanged) chosen.setAttribute("selected", "selected");
    });

    // Un SEUL dispatch, sur le dernier élément modifié.
    //
    // L'échelonnement précédent déclenchait un fetch par axe, chacun annulant
    // le précédent. L'annulation n'est pas fiable une fois la réponse en vol :
    // une réponse périmée pouvait arriver APRÈS la récente et écraser l'état.
    // Les valeurs étant toutes posées ci-dessus, un dispatch unique porte déjà
    // la combinaison complète.
    if (lastChanged && !silent) dispatchNativeChange(lastChanged);
  }

  function updateCustomSizeAvailability(wrapper, variants, groups, desiredByPos) {
    const sizeEl = wrapper.querySelector("[data-cv-size]");
    if (!sizeEl) return;

    const sizeGroup = findGroup(groups, "Size");
    const frameGroup = findFrameGroup(groups);
    const styleGroup = findGroup(groups, "Style");
    if (!sizeGroup || !frameGroup) return;

    const idxSize = sizeGroup.position - 1;
    const idxFrame = frameGroup.position - 1;
    const idxStyle = styleGroup ? styleGroup.position - 1 : null;

    const desiredFrame = desiredByPos[idxFrame];
    const desiredStyle = idxStyle != null ? desiredByPos[idxStyle] : null;

    const allowed = new Set();
    const wantedFrame = normalize(desiredFrame);
    const wantedStyle = desiredStyle != null ? normalize(desiredStyle) : null;

    for (const v of variants) {
      const opts = v.options || [];
      if (normalize(opts[idxFrame]) !== wantedFrame) continue;

      if (idxStyle != null && wantedStyle != null && wantedStyle !== "") {
        if (normalize(opts[idxStyle]) !== wantedStyle) continue;
      }

      allowed.add(normalize(opts[idxSize]));
    }

    if (!allowed.size) return;

    [...sizeEl.options].forEach((o) => {
      o.disabled = !allowed.has(normalize(o.value));
    });

    const selectedOpt = sizeEl.selectedOptions?.[0];
    if (selectedOpt && selectedOpt.disabled) {
      const firstEnabled = [...sizeEl.options].find((o) => !o.disabled);
      if (firstEnabled) sizeEl.value = firstEnabled.value;
    }

    // Mirror disabled state onto custom dropdown options
    const sizeDropdown = wrapper.querySelector('.custom-dropdown[data-dropdown="size"]');
    if (sizeDropdown) {
      [...sizeEl.options].forEach((o) => {
        const li = sizeDropdown.querySelector(`.custom-dropdown__option[data-value="${CSS.escape(o.value)}"]`);
        if (li) {
          li.classList.toggle("is-disabled", o.disabled);
          li.setAttribute("aria-disabled", o.disabled ? "true" : "false");
        }
      });
      syncDropdownFromSelect(sizeDropdown, sizeEl);
    }
  }

  function applyCustomToNative(wrapper) {
    const productInfo = closestProductInfo(wrapper);
    const variantSelects = getVariantSelects(productInfo);
    if (!variantSelects) return;

    const variants = getVariants(variantSelects);
    if (!variants?.length) return;

    const groups = getOptionGroups(variantSelects);
    const sizeGroup = findGroup(groups, "Size");
    const frameGroup = findFrameGroup(groups);
    const styleGroup = findGroup(groups, "Style");
    if (!sizeGroup || !frameGroup) return;

    const sizeUI = wrapper.querySelector("[data-cv-size]")?.value;
    const styleUI = wrapper.querySelector("[data-cv-frame]")?.value;

    const sizeValue = pickExisting(sizeGroup.values, [sizeUI]);
    const framedValue = getFramedValue(frameGroup);
    const unframedValue = getUnframedValue(frameGroup);

    const isFramed = !isNoFrameValue(styleUI);
    const frameValue = isFramed ? framedValue : unframedValue;
    if (!sizeValue || !frameValue) return;

    // The UI already holds the exact Shopify "Style" value, so this is a lookup,
    // not a reconstruction.
    const styleValue = styleGroup ? pickExisting(styleGroup.values, [styleUI]) : null;

    const optionLen = (variants[0].options || []).length;
    const current = new Array(optionLen);
    groups.forEach((g) => (current[g.position - 1] = getSelectedValue(g)));

    const desiredByPos = current.slice();
    desiredByPos[sizeGroup.position - 1] = sizeValue;
    desiredByPos[frameGroup.position - 1] = frameValue;
    if (styleGroup && styleValue != null) desiredByPos[styleGroup.position - 1] = styleValue;

    updateCustomSizeAvailability(wrapper, variants, groups, desiredByPos);

    const finalSizeUI = wrapper.querySelector("[data-cv-size]")?.value;
    const finalSizeValue = pickExisting(sizeGroup.values, [finalSizeUI]) || sizeValue;
    desiredByPos[sizeGroup.position - 1] = finalSizeValue;

    let target = findVariantByOptions(variants, desiredByPos);

    if (!target) {
      const idxFrame = frameGroup.position - 1;
      const idxStyle = styleGroup ? styleGroup.position - 1 : null;
      const wantedFrame = desiredByPos[idxFrame];
      const wantedStyle = idxStyle != null ? desiredByPos[idxStyle] : null;

      const pool = variants.filter((v) => {
        const o = v.options || [];
        if (normalize(o[idxFrame]) !== normalize(wantedFrame)) return false;
        if (idxStyle != null && wantedStyle != null && wantedStyle !== "") {
          if (normalize(o[idxStyle]) !== normalize(wantedStyle)) return false;
        }
        return true;
      });

      target = pool.find((v) => v.available) || pool[0] || null;
    }

    if (!target) return;

    syncing.add(wrapper);
    setNativeToVariant(variantSelects, groups, target);

    setTimeout(() => {
      const selected = getSelectedVariantFromScript(variantSelects);
      if (selected === null) {
        const recovered = bestVariantForCurrentSelection(variants, groups);
        if (recovered) setNativeToVariant(variantSelects, groups, recovered);
      }
      syncing.delete(wrapper);
    }, 200);
  }

  function syncCustomFromNative(productInfo) {
    const wrapper = productInfo?.querySelector("[data-custom-variants]");
    if (!wrapper || syncing.has(wrapper)) return;

    const variantSelects = getVariantSelects(productInfo);
    if (!variantSelects) return;

    const variants = getVariants(variantSelects);
    const groups = getOptionGroups(variantSelects);
    if (!variants?.length || !groups?.length) return;

    const selected = getSelectedVariantFromScript(variantSelects);
    if (selected === null) {
      const recovered = bestVariantForCurrentSelection(variants, groups);
      if (recovered) {
        syncing.add(wrapper);
        setNativeToVariant(variantSelects, groups, recovered);
        setTimeout(() => syncing.delete(wrapper), 200);
      }
    }

    const sizeGroup = findGroup(groups, "Size");
    const frameGroup = findFrameGroup(groups);
    const styleGroup = findGroup(groups, "Style");

    const size = getSelectedValue(sizeGroup);
    const frame = getSelectedValue(frameGroup);
    const style = getSelectedValue(styleGroup);

    const sizeEl = wrapper.querySelector("[data-cv-size]");
    if (sizeEl && size != null && sizeEl.value !== size) sizeEl.value = size;

    // A value is unframed if it explicitly says "unframed" OR if it simply
    // does not contain "framed" at all (e.g. "Fine Art Print").
    const isUnframed =
      frame &&
      (normalize(frame).includes("unframed") || !normalize(frame).includes("framed"));

    // Mirror the native "Style" value straight into the custom select.
    const frameEl = wrapper.querySelector("[data-cv-frame]");
    if (frameEl) {
      const next = isUnframed
        ? pickExisting(styleGroup?.values || [], ["-", "\u2013", "\u2014", ""]) || frameEl.value
        : style;
      if (next != null && frameEl.value !== next) frameEl.value = next;
    }

    // Reflect hidden select values in the visible custom dropdowns
    syncAllDropdowns(wrapper);
  }

  function initializeDefaults(wrapper) {
    const productInfo = closestProductInfo(wrapper);
    const variantSelects = getVariantSelects(productInfo);
    if (!variantSelects) return;

    const variants = getVariants(variantSelects);
    const groups = getOptionGroups(variantSelects);
    if (!variants?.length || !groups?.length) return;

    const sizeGroup = findGroup(groups, "Size");
    if (!sizeGroup?.values?.length) return;

    const sizeEl = wrapper.querySelector("[data-cv-size]");
    if (sizeEl && !sizeEl.value) {
      sizeEl.value = sizeGroup.values[0];
    }

    applyCustomToNative(wrapper);
    syncAllDropdowns(wrapper);
  }

  // =========================================================
  // CUSTOM DROPDOWN — helper functions
  // =========================================================

  /**
   * Reflect the hidden select's current value in the visible custom dropdown:
   * updates active state on options and refreshes the trigger's label/swatch.
   */
  function syncDropdownFromSelect(dropdown, select) {
    const value = select.value;

    // Mark the matching option as active
    dropdown.querySelectorAll(".custom-dropdown__option").forEach((opt) => {
      const isActive = opt.dataset.value === value;
      opt.classList.toggle("is-active", isActive);
      opt.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    const activeOpt = dropdown.querySelector(`.custom-dropdown__option[data-value="${CSS.escape(value)}"]`);
    if (!activeOpt) return;

    // Update trigger label text
    const valueEl = dropdown.querySelector(".custom-dropdown__value");
    if (valueEl) {
      const labelEl = activeOpt.querySelector(".custom-dropdown__option-label");
      valueEl.textContent = labelEl ? labelEl.textContent : activeOpt.textContent.trim();
    }

    // Update swatch in trigger (frame dropdown only)
    const triggerSwatch = dropdown.querySelector(".custom-dropdown__trigger .custom-dropdown__swatch");
    const optSwatch = activeOpt.querySelector(".custom-dropdown__swatch");
    if (triggerSwatch && optSwatch) {
      triggerSwatch.className = optSwatch.className;
    }

    // Support image swatches
    const triggerImg = dropdown.querySelector(".custom-dropdown__trigger .custom-dropdown__swatch-img");
    const optImg = activeOpt.querySelector(".custom-dropdown__swatch-img");
    if (triggerImg && optImg) {
      triggerImg.src = optImg.src;
      triggerImg.alt = optImg.alt;
    }
  }

  /** Sync all custom dropdowns in a wrapper from their hidden selects. */
  function syncAllDropdowns(wrapper) {
    wrapper.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      const id = dropdown.dataset.dropdown;
      const select = wrapper.querySelector(`[data-cv-${id}]`);
      if (select) syncDropdownFromSelect(dropdown, select);
    });
  }

  /**
   * Wire up open/close behaviour and option selection for every custom
   * dropdown inside `wrapper`. Must be called once per wrapper on init.
   */
  /**
   * Scroll an option into view inside its panel.
   *
   * preventVariantScroll() patches Element.prototype.scrollIntoView and
   * suppresses it for 5 s after any variant change — exactly when someone is
   * arrowing through this list — so the panel is scrolled directly instead.
   */
  function scrollOptionIntoView(list, option) {
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }

  function selectableOptions(list) {
    return [...list.querySelectorAll(".custom-dropdown__option")].filter(
      (o) => !o.classList.contains("is-disabled")
    );
  }

  /**
   * Move the listbox's virtual focus. The panel itself holds DOM focus while
   * aria-activedescendant names the option a screen reader should announce.
   */
  function setActiveOption(dropdown, option) {
    const list = dropdown.querySelector(".custom-dropdown__options");
    if (!list) return;

    list
      .querySelectorAll(".custom-dropdown__option.is-focused")
      .forEach((o) => o.classList.remove("is-focused"));

    if (!option) {
      list.removeAttribute("aria-activedescendant");
      return;
    }

    option.classList.add("is-focused");
    list.setAttribute("aria-activedescendant", option.id);
    scrollOptionIntoView(list, option);
  }

  function initCustomDropdowns(wrapper) {
    const closeAll = (returnFocus) => {
      wrapper.querySelectorAll(".custom-dropdown.is-open").forEach((d) => {
        d.classList.remove("is-open");
        const t = d.querySelector(".custom-dropdown__trigger");
        t?.setAttribute("aria-expanded", "false");
        setActiveOption(d, null);
        if (returnFocus === true) t?.focus();
        // Remove opens-up only after the CSS transition finishes (150 ms).
        // Removing it immediately snaps the transform target to the downward
        // base value, causing the panel to flash toward the bottom before
        // fading out.
        if (d.classList.contains("opens-up")) {
          setTimeout(() => d.classList.remove("opens-up"), 150);
        }
      });
    };

    wrapper.querySelectorAll(".custom-dropdown").forEach((dropdown) => {
      const trigger = dropdown.querySelector(".custom-dropdown__trigger");
      const optionsList = dropdown.querySelector(".custom-dropdown__options");
      const dropdownId = dropdown.dataset.dropdown;
      const select = wrapper.querySelector(`[data-cv-${dropdownId}]`);

      if (!trigger || !optionsList || !select) return;

      const open = () => {
        // Decide whether to open upward or downward based on available space
        const triggerRect = trigger.getBoundingClientRect();
        const panelMaxHeight = 240; // matches CSS max-height of 24rem
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        if (spaceBelow < panelMaxHeight && spaceAbove > spaceBelow) {
          dropdown.classList.add("opens-up");
        }
        dropdown.classList.add("is-open");
        trigger.setAttribute("aria-expanded", "true");
      };

      /** Open from the keyboard, then put virtual focus on an option. */
      const openFromKeyboard = (edge) => {
        closeAll();
        open();

        const items = selectableOptions(optionsList);
        if (!items.length) return;

        let target = items[0];
        if (edge === "last") {
          target = items[items.length - 1];
        } else if (edge !== "first") {
          const active = optionsList.querySelector(".custom-dropdown__option.is-active");
          if (active && !active.classList.contains("is-disabled")) target = active;
        }

        setActiveOption(dropdown, target);
        optionsList.focus();
      };

      const commit = (option) => {
        if (!option || option.classList.contains("is-disabled")) return;

        select.value = option.dataset.value;
        syncDropdownFromSelect(dropdown, select);
        closeAll(true);

        // Dispatch change so the main sync logic picks it up
        select.dispatchEvent(new Event("change", { bubbles: true }));
      };

      // Toggle open / close
      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        const wasOpen = dropdown.classList.contains("is-open");
        closeAll();
        if (!wasOpen) open();
      });

      trigger.addEventListener("keydown", (e) => {
        switch (e.key) {
          case "ArrowDown":
          case "Enter":
          case " ":
            // preventDefault stops the button's synthesised click, which would
            // toggle the panel straight back shut.
            e.preventDefault();
            openFromKeyboard();
            break;
          case "ArrowUp":
          case "End":
            e.preventDefault();
            openFromKeyboard("last");
            break;
          case "Home":
            e.preventDefault();
            openFromKeyboard("first");
            break;
        }
      });

      optionsList.addEventListener("keydown", (e) => {
        const items = selectableOptions(optionsList);
        if (!items.length) return;

        const active = optionsList.querySelector(".custom-dropdown__option.is-focused");
        const i = active ? items.indexOf(active) : -1;

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setActiveOption(dropdown, items[Math.min(i + 1, items.length - 1)]);
            break;
          case "ArrowUp":
            e.preventDefault();
            setActiveOption(dropdown, items[Math.max(i - 1, 0)]);
            break;
          case "Home":
            e.preventDefault();
            setActiveOption(dropdown, items[0]);
            break;
          case "End":
            e.preventDefault();
            setActiveOption(dropdown, items[items.length - 1]);
            break;
          case "Enter":
          case " ":
            e.preventDefault();
            commit(active);
            break;
          case "Escape":
            e.preventDefault();
            closeAll(true);
            break;
          case "Tab":
            // Let focus move on naturally, but never leave an orphaned panel open.
            closeAll();
            break;
        }
      });

      // Select an option
      optionsList.querySelectorAll(".custom-dropdown__option").forEach((option) => {
        option.addEventListener("click", () => commit(option));
      });
    });

    // Close on outside click
    document.addEventListener("click", () => closeAll());

    // Close on Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAll();
    });

    // Render initial state from the hidden selects
    syncAllDropdowns(wrapper);
  }

  // Prevent auto-scroll on variant change
  function preventVariantScroll() {
    let blockScroll = false;
    let blockScrollTimeout;

    const originalScrollTo = window.scrollTo.bind(window);
    const originalScroll = window.scroll.bind(window);

    // --- Block scrollIntoView during variant changes ---
    const originalScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function () {
      if (blockScroll) return;
      return originalScrollIntoView.apply(this, arguments);
    };

    // --- Block focus-induced scroll (the actual root cause) ---
    // product-info.js calls .focus() after re-rendering the section,
    // which makes the browser scroll the element into view.
    const originalFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function (options) {
      if (blockScroll) {
        return originalFocus.call(this, Object.assign({}, options, { preventScroll: true }));
      }
      return originalFocus.call(this, options);
    };

    // --- Block programmatic scrollTo AND scroll during variant changes ---
    // Both must be overridden: they are separate property references in browsers.
    // smooth-variant-media.js makes setActiveMedia async (waits for image load),
    // so the scrollTo inside it can fire well after the short protection window.
    window.scrollTo = function (...args) {
      if (blockScroll) return;
      return originalScrollTo(...args);
    };
    window.scroll = function (...args) {
      if (blockScroll) return;
      return originalScroll(...args);
    };

    // Start blocking programmatic scrolls for 5 s.
    // User's own scroll (wheel/touch/keyboard) is never blocked — we only
    // suppress JS-initiated window.scrollTo / scrollIntoView / focus-scroll.
    const startProtection = () => {
      clearTimeout(blockScrollTimeout);
      blockScroll = true;
      blockScrollTimeout = setTimeout(() => { blockScroll = false; }, 5000);
    };

    // change/input fires synchronously when the user selects a variant.
    document.addEventListener('change', (e) => {
      if (
        e.target.closest('[data-custom-variants]') ||
        e.target.closest('variant-selects')
      ) startProtection();
    }, true);
    document.addEventListener('input', (e) => {
      if (
        e.target.closest('[data-custom-variants]') ||
        e.target.closest('variant-selects')
      ) startProtection();
    }, true);

    // Extend the block window on Dawn's async variantChange event (fires after fetch).
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        if (blockScroll) {
          clearTimeout(blockScrollTimeout);
          blockScrollTimeout = setTimeout(() => { blockScroll = false; }, 5000);
        }
      });
    }
  }

  function handleCustomUIChange(e) {
    const wrapper = e.target.closest("[data-custom-variants]");
    if (!wrapper) return;

    if (e.target.matches("[data-cv-size],[data-cv-frame]")) {
      e.preventDefault();
      setTimeout(() => {
        applyCustomToNative(wrapper);
      }, 10);
    }
  }

  // Échec du rendu de section : product-info a rendu la main sur le bouton
  // d'achat, à nous de remettre l'interface sur la variante réellement chargée.
  // Sans ça, le libellé continuerait d'annoncer une variante que le panier ne
  // recevrait pas. Le script [data-selected-variant] n'a pas été remplacé
  // puisque la requête a échoué : il porte donc bien l'état chargé.
  document.addEventListener("variant:update-failed", (e) => {
    const productInfo = closestProductInfo(e.target);
    if (!productInfo) return;

    const variantSelects = getVariantSelects(productInfo);
    const groups = getOptionGroups(variantSelects);
    const loaded = getSelectedVariantFromScript(variantSelects);
    if (!variantSelects || !groups?.length || !loaded) return;

    setNativeToVariant(variantSelects, groups, loaded, true);

    // Lever le verrou avant de recaler l'interface. `applyCustomToNative` tient
    // `syncing` pendant 200 ms pour empêcher un recalage pendant ses propres
    // écritures ; on est en plein dedans, puisque l'échec arrive quelques
    // millisecondes après. Sans cette levée, syncCustomFromNative sort dès sa
    // première ligne : les <select> de Dawn étaient bien remis sur la variante
    // chargée, mais le libellé, le select caché et l'option active restaient
    // sur la taille demandée. La séquence est terminée — en échec, mais
    // terminée : garder le verrou n'a plus d'objet.
    const wrapper = productInfo.querySelector("[data-custom-variants]");
    if (wrapper) {
      syncing.delete(wrapper);
      syncCustomFromNative(productInfo);
    }

    // Recalage effectué : product-info peut rendre la main sur le bouton.
    // Tout retour anticipé plus haut le laisse volontairement désactivé.
    e.preventDefault();
  });

  document.addEventListener("change", handleCustomUIChange, true);
  document.addEventListener("input", handleCustomUIChange, true);

  function init() {
    patchVariantSelectsReset();
    patchDoubleSubscription();
    preventVariantScroll();

    document.querySelectorAll("product-info").forEach((pi) => {
      const wrapper = pi.querySelector("[data-custom-variants]");
      if (wrapper) {
        initCustomDropdowns(wrapper);

        const urlParams = new URLSearchParams(window.location.search);
        const hasVariantParam = urlParams.has('variant');

        if (!hasVariantParam) {
          initializeDefaults(wrapper);
        } else {
          syncCustomFromNative(pi);
        }
      } else {
        syncCustomFromNative(pi);
      }
    });

    if (typeof window.subscribe === "function" && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const sectionId = data?.sectionId;
        const pi = sectionId
          ? document.querySelector(`product-info[data-section="${sectionId}"]`)
          : document.querySelector("product-info");
        syncCustomFromNative(pi);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Filet sur l'ordre de chargement : si global.js n'a pas encore défini
  // <variant-selects> au moment de init(), le correctif s'applique dès qu'il
  // l'est. La fonction est idempotente.
  window.customElements?.whenDefined("variant-selects").then(patchVariantSelectsReset);
  window.customElements?.whenDefined("product-info").then(patchDoubleSubscription);

  document.addEventListener("shopify:section:load", () => {
    document.querySelectorAll("product-info").forEach((pi) => {
      const wrapper = pi.querySelector("[data-custom-variants]");
      if (wrapper) initCustomDropdowns(wrapper);
      syncCustomFromNative(pi);
    });
  });

  // smooth-variant-media.js handles the slide animation by shadowing .remove()
  // directly on each active <li> before updateMedia() runs. The removeChild
  // approach cannot intercept element.remove() (a native DOM operation), so
  // no additional crossfade logic is needed here.
  function setupCrossfade() {}
  setTimeout(setupCrossfade, 300);
  document.addEventListener('shopify:section:load', () => setTimeout(setupCrossfade, 300));
})();

/**
 * Custom Add to Cart Button - Price Display
 * This script adds price to the button and maintains it across variant changes
 * WITHOUT modifying core Dawn files
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    addToCartText: 'Add to cart',
    arrow: '  →',
    updateDelay: 0 // No delay for instant update
  };

  // Wait for everything to load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    
    // Find the product form
    const productForm = document.querySelector('product-form');
    if (!productForm) {
      return;
    }

    // Get button elements
    const submitButton = productForm.querySelector('[type="submit"]');
    const buttonTextSpan = submitButton?.querySelector('span');
    
    if (!submitButton || !buttonTextSpan) {
      return;
    }


    // Store original toggle method
    const originalToggleSubmitButton = productForm.toggleSubmitButton;
    
    // Override the toggleSubmitButton method
    productForm.toggleSubmitButton = function(disable = true, text) {
      
      if (disable) {
        submitButton.setAttribute('disabled', 'disabled');
        if (text) {
          buttonTextSpan.textContent = text;
          // A verdict of "sold out" / "unavailable". Setting textContent above
          // swaps the label but not the price sitting beside it, and every
          // updateButtonWithPrice call site skips a disabled button — so
          // nothing else would take it down, and the button would go on quoting
          // the last in-stock variant the visitor happened to look at.
          //
          // Guarded on `text` on purpose: product-info's resetProductFormState()
          // also disables the button, with no text, at the START of every
          // variant change while the section is refetched. Clearing there would
          // blink the price off and back on at each click.
          clearButtonPrice(submitButton);
        }
      } else {
        submitButton.removeAttribute('disabled');
        
        // ALWAYS update with price when enabling, regardless of text parameter
        // This fixes the "sold out" sticking issue
        updateButtonWithPrice(productForm, buttonTextSpan);
      }
    };

    // Watch for variant changes via the variant input
    const variantInput = productForm.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', function() {
        // Immediate update, no delay
        if (!submitButton.hasAttribute('disabled')) {
          updateButtonWithPrice(productForm, buttonTextSpan);
        }
      });
    }

    // Also listen for PUB_SUB_EVENTS if available
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, function() {
        // Immediate update, no delay
        if (!submitButton.hasAttribute('disabled')) {
          updateButtonWithPrice(productForm, buttonTextSpan);
        }
      });
    }

    // Watch for any text changes to the button (as a fallback)
    const buttonObserver = new MutationObserver(function(mutations) {
      for (let mutation of mutations) {
        const textContent = buttonTextSpan.textContent.trim();
        
        // Only update if button was reset to default "Add to cart" text
        // and button is not disabled
        if ((textContent === CONFIG.addToCartText || 
             textContent === window.variantStrings?.addToCart) &&
            !submitButton.hasAttribute('disabled')) {
          updateButtonWithPrice(productForm, buttonTextSpan);
          break;
        }
      }
    });

    buttonObserver.observe(buttonTextSpan, {
      characterData: true,
      childList: true,
      subtree: true
    });

    // Initial update - immediate, no delay
    if (!submitButton.hasAttribute('disabled')) {
      updateButtonWithPrice(productForm, buttonTextSpan);
    }
  }

  /**
   * Take the price back off the add-to-cart button.
   *
   * The price is built into a .button-price-wrapper that sits NEXT TO the
   * button's text span, not inside it — so relabelling the button, whether by
   * Dawn or by us, leaves the price standing. Every path that stops short of
   * pricing a variant has to call this, or the button keeps quoting whichever
   * variant was priced last.
   */
  function clearButtonPrice(submitButton) {
    submitButton?.querySelector(".button-price-wrapper")?.remove();
  }

  function updateButtonWithPrice(productForm, buttonTextSpan) {
    
    // Get submit button from the form
    const submitButton = productForm.querySelector('[type="submit"]');
    if (!submitButton) {
      return;
    }
    
    // Get variant input
    const variantInput = productForm.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) {
      clearButtonPrice(submitButton);
      return;
    }

    const variantId = variantInput.value;

    // Get product info
    const productInfo = productForm.closest('product-info');
    if (!productInfo) {
      return;
    }

    // Get product data - try multiple sources
    let productData = null;
    let variants = null;
    
    // Method 1: Check for product data in product-info script tag
    const productDataScript = productInfo.querySelector('script[type="application/json"]');
    if (productDataScript) {
      try {
        productData = JSON.parse(productDataScript.textContent);
        
        // Handle different data structures
        if (Array.isArray(productData)) {
          // Data is directly an array of variants
          variants = productData;
        } else if (productData.variants) {
          // Data is object with variants property
          variants = productData.variants;
        } else if (productData.product && productData.product.variants) {
          // Data is nested under product
          variants = productData.product.variants;
        }
      } catch (e) {
      }
    }
    
    // Method 2: Check for global product object from ShopifyAnalytics
    if (!variants && typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta?.product) {
      const analyticsProduct = ShopifyAnalytics.meta.product;
      variants = analyticsProduct.variants;
    }
    
    // Method 3: Try to get from window
    if (!variants && typeof window.product !== 'undefined') {
      if (Array.isArray(window.product)) {
        variants = window.product;
      } else if (window.product.variants) {
        variants = window.product.variants;
      }
    }

    if (!variants || !variants.length) {
      return;
    }



    // Find the variant - try multiple ID formats
    let variant = null;
    
    // Try as number
    const variantIdNum = parseInt(variantId);
    variant = variants.find(v => v.id === variantIdNum || v.id === variantId);
    
    // Try as string
    if (!variant) {
      variant = variants.find(v => String(v.id) === String(variantId));
    }
    
    // Repli : retrouver la variante par les options selectionnees.
    //
    // La version precedente lisait les selecteurs dans l'ordre du DOM et
    // comparait les libelles tels quels. Trois hypotheses fragiles :
    //
    //  - que l'ordre du DOM corresponde a l'ordre des options Shopify ;
    //  - que le selecteur mixte (select, radio coche, [data-selected-option])
    //    ramene exactement une entree par option, sans doublon ;
    //  - que les libelles soient identiques au caractere pres, alors que les
    //    tailles contiennent « × » et des espaces insecables.
    //
    // On passe donc par les helpers du fichier : getOptionGroups lit la POSITION
    // reelle de chaque option et trie dessus, et findVariantByOptions normalise
    // les deux cotes avant de comparer.
    if (!variant) {
      const variantSelects = getVariantSelects(productInfo);
      const groups = variantSelects ? getOptionGroups(variantSelects) : [];

      if (groups.length) {
        const desired = new Array((variants[0].options || []).length);
        groups.forEach((group) => {
          desired[group.position - 1] = getSelectedValue(group);
        });
        variant = findVariantByOptions(variants, desired);
      }
    }
    
    if (!variant) {
      clearButtonPrice(submitButton);
      return;
    }

    if (!variant.available) {
      clearButtonPrice(submitButton);
      return;
    }

    // Format money - create our own formatter since Shopify.formatMoney doesn't exist
    const formatMoney = function(cents) {
      // Get currency info from Shopify
      const currency = (typeof Shopify !== 'undefined' && Shopify.currency) 
        ? Shopify.currency.active 
        : 'EUR';
      
      
      const amount = cents / 100;
      
      // Format based on currency
      if (currency === 'EUR') {
        // Euro format: 29,99 € (French/EU standard - symbol after).
        // 28/10/2026 : prix rond sans « ,00 » (40 €), comme money_without_trailing_zeros
        // dans snippets/price.liquid ; les centimes restent quand il y en a (40,50 €).
        return (Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace('.', ',')) + ' €';
      } else if (currency === 'USD' || currency === 'CAD' || currency === 'AUD') {
        // Dollar format: $29.99
        return '$' + amount.toFixed(2);
      } else if (currency === 'GBP') {
        // Pound format: £29.99
        return '£' + amount.toFixed(2);
      } else {
        // Generic format with currency code
        return amount.toFixed(2) + ' ' + currency;
      }
    };

    // Build the button content with arrow inside text span, price wrapper outside
    const addToCartText = window.variantStrings?.addToCart || CONFIG.addToCartText;
    const price = formatMoney(variant.price);
    const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
      ? formatMoney(variant.compare_at_price)
      : null;



    // Clear the button text span and rebuild with text + arrow
    buttonTextSpan.innerHTML = '';
    
    // Add the "Add to cart" text
    const textNode = document.createTextNode(addToCartText);
    buttonTextSpan.appendChild(textNode);

    // Spacer qui grandit au survol pour faire glisser la fleche jusqu'au prix
    const arrowSpacer = document.createElement('span');
    arrowSpacer.className = 'button-arrow-spacer';
    arrowSpacer.setAttribute('aria-hidden', 'true');
    buttonTextSpan.appendChild(arrowSpacer);

    // Add arrow inside the button text span
    const arrowSpan = document.createElement('span');
    arrowSpan.className = 'text-arrow';
    arrowSpan.textContent = CONFIG.arrow;
    buttonTextSpan.appendChild(arrowSpan);
    
    
    // Remove any existing price wrapper (sibling)
    let existingPriceWrapper = submitButton.querySelector('.button-price-wrapper');
    if (existingPriceWrapper) {
      existingPriceWrapper.remove();
    }
    
    // Create price wrapper as sibling to buttonTextSpan
    const priceWrapper = document.createElement('span');
    priceWrapper.className = 'button-price-wrapper';
    
    if (comparePrice) {
      const comparePriceSpan = document.createElement('span');
      comparePriceSpan.className = 'button-price button-price--compare';
      comparePriceSpan.textContent = '(' + comparePrice + ')';
      priceWrapper.appendChild(comparePriceSpan);
    }
    
    const regularPriceSpan = document.createElement('span');
    regularPriceSpan.className = 'button-price button-price--regular';
    regularPriceSpan.textContent = '(' + price + ')';
    priceWrapper.appendChild(regularPriceSpan);
    
    // Find the loading spinner to insert before it
    const loadingSpinner = submitButton.querySelector('.loading__spinner');
    
    if (loadingSpinner) {
      // Insert price wrapper before the loading spinner
      submitButton.insertBefore(priceWrapper, loadingSpinner);
    } else {
      // Fallback: insert after button text span
      buttonTextSpan.after(priceWrapper);
    }


  }

  // Expose for debugging
  window.debugButtonPrice = function() {
    const productForm = document.querySelector('product-form');
    const buttonTextSpan = productForm?.querySelector('[type="submit"] span');
    if (productForm && buttonTextSpan) {
      updateButtonWithPrice(productForm, buttonTextSpan);
    }
  };
  
  // Additional debug helper
  window.debugProductData = function() {
    
    const productForm = document.querySelector('product-form');
    const variantInput = productForm?.querySelector('[name="id"]');
    const productInfo = productForm?.closest('product-info');
    

    
    // Check all possible product data sources
    const productDataScript = productInfo?.querySelector('script[type="application/json"]');
    if (productDataScript) {
      try {
        const data = JSON.parse(productDataScript.textContent);
        
        let variants = null;
        if (Array.isArray(data)) {
          variants = data;
        } else if (data.variants) {
          variants = data.variants;
        } else if (data.product && data.product.variants) {
          variants = data.product.variants;
        }
        

      } catch (e) {
      }
    }
  };
})();

/**
 * Variant Tooltip Drawer
 * Handles the slide-out panel for variant option information
 */

class VariantTooltipDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-tooltip-drawer]');
    if (!this.drawer) return;

    // IMPORTANT: Move drawer to body level to fix z-index stacking context issues
    if (this.drawer.parentElement.tagName !== 'BODY') {
      document.body.appendChild(this.drawer);
    }

    this.overlay = this.drawer.querySelector('[data-tooltip-overlay]');
    this.inner = this.drawer.querySelector('.variant-tooltip-drawer__inner');
    this.closeBtn = this.drawer.querySelector('[data-tooltip-close]');
    this.title = this.drawer.querySelector('[data-tooltip-title]');
    this.content = this.drawer.querySelector('[data-tooltip-content]');
    this.triggers = document.querySelectorAll('[data-tooltip-trigger]');

    this.isOpen = false;
    this.currentTooltip = null;

    this.init();
  }

  init() {
    // Bind event listeners
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => this.handleTriggerClick(e));
    });

    this.closeBtn.addEventListener('click', () => this.close());
    
    // Close when clicking overlay (outside the panel)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Also close when clicking anywhere outside the drawer panel
    this.drawer.addEventListener('click', (e) => {
      if (e.target === this.drawer) {
        this.close();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Prevent scroll on drawer content from scrolling body
    this.content.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }

  handleTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const trigger = e.currentTarget;
    const tooltipType = trigger.dataset.tooltipTrigger;
    
    this.open(tooltipType);
  }

  open(tooltipType) {
    this.currentTooltip = tooltipType;
    
    // Load content
    this.loadContent(tooltipType);
    
    // Open drawer
    this.drawer.classList.add('is-open');
    document.body.classList.add('variant-tooltip-open');
    this.isOpen = true;

    // Trap focus inside drawer
    this.trapFocus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('variant-tooltip-open');
    this.isOpen = false;
    this.currentTooltip = null;

    // Return focus to trigger if possible
    this.returnFocus();
  }

  loadContent(tooltipType) {
    // Get template
    const template = document.getElementById(`tooltip-content-${tooltipType}`);
    
    if (!template) {
      console.error(`No template found for tooltip type: ${tooltipType}`);
      return;
    }

    // Set title
    const titles = {
      'size': 'Size Guide',
      'frame': 'Frame Guide'
    };
    
    this.title.textContent = titles[tooltipType] || 'Information';

    // Clone and insert content
    const clone = template.content.cloneNode(true);
    this.content.innerHTML = '';
    this.content.appendChild(clone);

    // Scroll to top
    this.content.scrollTop = 0;
  }

  trapFocus() {
    // Get all focusable elements
    const focusableElements = this.inner.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the close button
    this.closeBtn.focus();

    // Trap focus
    this.inner.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  returnFocus() {
    // Try to return focus to the trigger that opened this tooltip
    if (this.currentTooltip) {
      const trigger = document.querySelector(`[data-tooltip-trigger="${this.currentTooltip}"]`);
      if (trigger) {
        trigger.focus();
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantTooltipDrawer();
  });
} else {
  new VariantTooltipDrawer();
}

// Reinitialize if used in theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new VariantTooltipDrawer();
  });
}